import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { AlertTriangle } from 'lucide-react';

/**
 * Security Confirmation Dialog for critical Superadmin actions
 * Requires user to type a confirmation phrase
 */
export const SecurityConfirmDialog = ({ 
  open, 
  onOpenChange, 
  onConfirm, 
  title, 
  description, 
  confirmPhrase = "Ich verstehe die Konsequenzen",
  actionLabel = "Bestätigen",
  variant = "destructive"
}) => {
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    if (inputValue !== confirmPhrase) {
      return;
    }

    setLoading(true);
    try {
      await onConfirm();
      setInputValue('');
      onOpenChange(false);
    } catch (error) {
      // Error handling done by parent
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setInputValue('');
    onOpenChange(false);
  };

  const isValid = inputValue === confirmPhrase;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-red-600" />
            <DialogTitle className="text-red-600">{title}</DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="confirm-input">
              Bitte geben Sie zur Bestätigung ein:
            </Label>
            <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded font-mono text-sm">
              {confirmPhrase}
            </div>
            <Input
              id="confirm-input"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={confirmPhrase}
              className={inputValue && !isValid ? 'border-red-500' : ''}
              disabled={loading}
            />
            {inputValue && !isValid && (
              <p className="text-xs text-red-600">
                Der eingegebene Text stimmt nicht überein
              </p>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Abbrechen
          </Button>
          <Button
            variant={variant}
            onClick={handleConfirm}
            disabled={!isValid || loading}
          >
            {loading ? 'Wird ausgeführt...' : actionLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
